import { useCallback, useContext, useEffect, useState } from "react";
import { ImportService } from "../../../services/ImportService";
import ImportTemplateService from "../../../services/ImportTemplateService";
import RangedDatepicker from "../../shared/components/DateRangePicker";
import Dropdown from "../../shared/components/Dropdown";
import { ImportLogListResponse } from "../../shared/entities/ImportLogListResponse";
import { ImportTemplateListResponse } from "../../shared/entities/ImportTemplateListResponse";
import { TranslationService } from "../../../services/TranslationService";
import Table, { TableHeader } from "../../shared/Table";
import TableContext, { TableContextValues } from "../../task/TableContext";
import ImportLogItem from "./ImportLogItem";
import CompanyService from "../../../services/CompanyService";
import { ButtonTooltipIcon } from "../../shared/components/ButtonTooltipIcon";
import { useHistory } from "react-router-dom";
import ToastContext from "../../shared/bootstrap/Toast";

const ImportLog = () => {
  const { translate } = TranslationService;
  const { showToast } = useContext(ToastContext);
  const [response, setResponse] = useState<ImportLogListResponse>();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);
  const [currentPage, _setCurrentPage] = useState<number>(0);
  const [pageCount, setPageCount] = useState<number>(1);
  const [filters, setFilters] = useState<ImportListFilter>({});
  const history = useHistory();
  const pagesize = 20;

  const [firstImport, setFirstImport] = useState<number>(0);
  const [exporting, setExporting] = useState<boolean>(false);
  const [runningIOUpdate, setRunningIOUpdate] = useState<boolean>(false);

  const requestData = async () => {
    setLoading(true);
    const result = await ImportService.getList({
      ...filters,
      page: currentPage,
      pagesize
    });
    if (result instanceof Error) {
      setError(true);
      setLoading(false);
      return;
    }
    const totalPages = Math.ceil(result.itemCount / pagesize);
    setResponse(result);
    setPageCount(totalPages);
    setLoading(false);
    setError(false);
  };

  const requestDataCallback = useCallback(requestData, [currentPage, filters]);
  useEffect(() => {
    requestDataCallback();
  }, [currentPage, requestDataCallback]);

  const setCurrentPage = (page: number) => {
    if (page >= 0 && page - 1 <= pageCount) {
      _setCurrentPage(page);
    }
  };

  const applyFilters = (filters: ImportListFilter) => {
    setCurrentPage(0);
    setFilters(filters);
    setFirstImport(0);
  };

  const doExport = async () => {
    setExporting(true);
    try {
      if (response) {
        await ImportService.export(
          { ...filters, page: 1, pagesize: 0 },
          response.itemCount
        );
      }
    } catch (error) {
      showToast(translate.ErrorProcessingRequest, undefined, "danger");
    } finally {
      setExporting(false);
    }
  };

  const runIOUpdate = async () => {
    const confirmMessage =
      translate.IOUpdateNow || "¿Desea ejecutar la actualización IO ahora?";
    if (!window.confirm(confirmMessage)) {
      return;
    }
    setRunningIOUpdate(true);
    try {
      const result = await ImportTemplateService.runIOUpdate();
      if (result instanceof Error) {
        showToast(translate.ErrorProcessingRequest, undefined, "danger");
      } else {
        // El servidor puede retornar Response: 'Ok' o 'Fail-Pending'
        const response = result as { Response?: string };
        if (response.Response === "Ok") {
          showToast(
            translate.ProcessRunning || "Proceso en ejecución",
            undefined,
            "info"
          );
        } else if (response.Response === "Fail-Pending") {
          showToast(
            translate.ProcessPending || "Proceso pendiente",
            undefined,
            "warning"
          );
        }
      }
    } catch (error) {
      showToast(translate.ErrorProcessingRequest, undefined, "danger");
    } finally {
      setRunningIOUpdate(false);
    }
  };

  useEffect(() => {
    //set the first import that has insert process OK
    if (response && response.list && firstImport === 0) {
      var existFirstElement = response.list.find((x) => x.total > 0);
      if (existFirstElement) {
        setFirstImport(existFirstElement.ImportID!);
      }
    }
  }, [response, firstImport]);

  const tableContext = new ImportTableContextValues(
    response?.lastcompleteimportid ?? null
  );
  tableContext.loading = loading;
  tableContext.error = error;
  tableContext.response = response;
  tableContext.currentPage = currentPage;
  tableContext.setCurrentPage = setCurrentPage;
  tableContext.pageCount = pageCount;
  tableContext.reload = requestDataCallback;
  tableContext.applyFilters = applyFilters;
  tableContext.isFirstImport = firstImport;

  const tableHead: TableHeader[] = [
    new TableHeader("type", translate.Type, false, false, "medium-text"),
    new TableHeader(
      "startdate",
      translate.StartDate,
      false,
      false,
      "short-text"
    ),
    new TableHeader(
      "lastStatus",
      translate.LastStatus,
      false,
      false,
      "short-text"
    ),
    new TableHeader("status", translate.Status, false, false, "medium-text"),
    new TableHeader("result", translate.Result, false, false, "w-300px"),
    new TableHeader("user", translate.User, false, false, "short-text"),
    new TableHeader(
      "process",
      translate.ProcessNoun,
      false,
      false,
      "long-text"
    ),
    new TableHeader("file", translate.File, false, false, "long-text"),
    new TableHeader("actions", "", false, false)
  ];

  const goToDictionaries = () =>
    history.push(`/${TranslationService.currentLanguage}/importdictionary`);
  return (
    <div className="main-layout-container mx-3">
      <div className="main-panel">
        <h4 className="mb-2">{TranslationService.translate.ImportLog}</h4>
        <div className="card">
          <div className="d-flex align-items-center justify-content-end mb-2 gap-2">
            <button
              className="btn btn-secondary"
              onClick={runIOUpdate}
              disabled={runningIOUpdate}
            >
              {runningIOUpdate ? (
                <>
                  <i className="fas fa-spinner-third fa-spin me-2"></i>
                  {translate.ProcessRunning || "Procesando..."}
                </>
              ) : (
                translate.IOUpdateNow || "Ejecutar actualización IO"
              )}
            </button>
            {CompanyService.canDo("export") && (
              <ButtonTooltipIcon
                title={translate.ExportToXls}
                icon="far fa-arrow-to-bottom"
                onClick={doExport}
                disabled={exporting}
                loading={exporting}
              />
            )}
            <button className="btn btn-secondary" onClick={goToDictionaries}>
              {translate.ViewDictionary}
            </button>
            <button className="btn btn-primary" onClick={requestDataCallback}>
              {translate.Refresh}
            </button>
          </div>

          <TableContext.Provider value={tableContext}>
            <ImportFilters />
            <Table headers={tableHead} item={ImportLogItem} />
          </TableContext.Provider>
        </div>
      </div>
    </div>
  );
};

export class ImportTableContextValues extends TableContextValues<
  ImportLogListResponse,
  ImportListFilter
> {
  constructor(public lastcompleteimportid: number | null) {
    super();
  }
}

type ImportListFilter = {
  from?: string;
  to?: string;
  importTemplateId?: number;
  type?: string;
};

const ImportFilters = () => {
  const { translate } = TranslationService;
  const tableContext = useContext(TableContext);
  const { applyFilters } = tableContext;

  const [filters, setFilters] = useState<ImportListFilter>({});
  const [filename, setFilename] = useState<string>("");
  const [importTemplates, setImportTemplates] =
    useState<ImportTemplateListResponse>();

  const onDateChange = useCallback((start?: Date, end?: Date) => {
    const newFilters = {
      ...filters,
      filename,
      from: start ? start.toJSON() : undefined,
      to: end ? parseDate(end).toJSON() : undefined
    };
    setFilters(newFilters);
    applyFilters(newFilters);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const parseDate = (value: Date): Date => {
    //set date without utc
    let hoursDiff = value.getHours() - value.getTimezoneOffset() / 60;
    let minutesDiff = (value.getHours() - value.getTimezoneOffset()) % 60;
    value.setHours(hoursDiff);
    value.setMinutes(minutesDiff);
    return value;
  };

  const fixedFiltersChange =
    (propName: string) => (newValue: string | number | undefined) => {
      const newFilters = {
        ...filters,
        filename,
        [propName]: newValue === "" ? undefined : newValue
      };
      setFilters(newFilters);
      applyFilters(newFilters);
    };

  const updateFilename = () => {
    const newFilters = { ...filters, filename };
    setFilters(newFilters);
    applyFilters(newFilters);
  };

  useEffect(() => {
    const getData = async () => {
      const result = await ImportTemplateService.getList();
      if (result instanceof Error) {
        return;
      }
      setImportTemplates(result);
    };
    getData();
  }, []);

  const importTypes = ImportService.getImportTypes().map((x) => ({
    text: TranslationService.getTranslation(x.text),
    value: x.value,
    key: x.value
  }));
  return (
    <div className="bg-light-gray mb-4">
      <div className="card-body">
        <div className="row">
          <div className="col-3">
            <label>{translate.ProcessNoun}</label>
            <Dropdown
              optionLabel={translate.FilterDefaultOptionLabel}
              onChange={fixedFiltersChange("importTemplateId")}
              items={
                importTemplates?.list.map((x) => ({
                  key: x.ImportTemplateID,
                  text: x.Name,
                  value: x.ImportTemplateID
                })) ?? []
              }
            />
          </div>
          <div className="col-3">
            <label>{translate.Type}</label>
            <Dropdown
              optionLabel={translate.FilterDefaultOptionLabel}
              onChange={fixedFiltersChange("type")}
              items={importTypes}
            />
          </div>
          <div className="col-3">
            <label>{translate.File}</label>
            <input
              onKeyPress={(event) => event.key === "Enter" && updateFilename()}
              onChange={(event) => setFilename(event.currentTarget.value)}
              className="form-control"
            />
          </div>
        </div>
        <div className="row justify-content-between mt-2">
          <label>{translate.Date}</label>
          <div className="col">
            <RangedDatepicker
              onRangeChange={onDateChange}
              defaultValue={null}
            />
          </div>
        </div>
      </div>
    </div>
  );
};

export default ImportLog;
